import type { BatchPreviewResponse } from '@store/generated/patient-requirement';

import type {
  BatchMessageCounts,
  ExcludedPatientView,
  ExclusionBreakdownEntry,
  RenderedMessagePreview,
} from '../components';

/**
 * The seam between the bulk-launch endpoints and the panels that render them.
 *
 * The DTO types below are HAND-WRITTEN from the shapes the backend engineer
 * posted on GDN-3228 and GDN-3450. They are not generated and not contract:
 * when the endpoints land, this file is the one place that changes, and the
 * panels do not. That is the entire point of the seam — the shape moved twice
 * already today while this was being built.
 */

/** Mirrors the posted batch resource. Provisional. */
export interface PostedBatch {
  batch_id: string;
  patient_requirement_definition_id: string;
  state: string;
  counts: {
    total_messages: number;
    queued: number;
    sent: number;
    delivered: number;
    failed: number;
    cancelled: number;
  };
  /** Patients from this batch who have since satisfied the requirement. */
  completed_patient_count?: number | null;
}

export interface BulkLaunchPreviewData {
  /** False when the preview is a floor and no launch should be offered. */
  canLaunch: boolean;
  isTruncated: boolean;
  eligiblePatients: number;
  alreadyCompletePatients: number;
  excludedPatients: number;
  messagesToSend: number;
  exclusionBreakdown?: ExclusionBreakdownEntry[];
  /** Undefined when the server sent no page. Absent is not empty. */
  excludedPatientsPage?: ExcludedPatientView[];
  messagePreview?: RenderedMessagePreview;
}

/**
 * True when the per-reason breakdown accounts for every excluded patient.
 *
 * The backend states this as a guarantee: one primary reason per patient, so
 * the breakdown sums to the total. Checking it is not distrust — it decides
 * what the panel can honestly say. A breakdown that does not sum is not a
 * smaller breakdown, it is a breakdown of an unknown subset, and rendering it
 * under "why are some left out" would present a partial answer as the whole
 * one. The panel falls back to naming patients instead, which is the same path
 * it takes when no breakdown is sent at all.
 *
 * `read` — the definition's open population at preview time — landed with the
 * merged contract, so the three-way sum I could not check in #2938 is checked
 * here too: eligible + already-complete + excluded must account for everyone
 * the preview read. A breakdown of an unknown subset is not a smaller
 * breakdown; it is a different question, and rendering it under "why are some
 * left out" answers the wrong one.
 *
 * Not checked when the preview is truncated: `read` is then a floor like
 * everything else, so the sum failing says nothing.
 */
export const exclusionBreakdownIsComplete = (preview: BatchPreviewResponse): boolean => {
  const breakdown = preview.patients.excluded_by_reason;
  if (!breakdown) return false;

  const entries = Object.values(breakdown);
  if (entries.length === 0) return false;

  if (entries.reduce((sum, count) => sum + count, 0) !== preview.patients.excluded) {
    return false;
  }

  if (preview.truncated === true) return true;

  const { eligible, already_complete: alreadyComplete, excluded, read } = preview.patients;
  return eligible + alreadyComplete + excluded === read;
};

/**
 * The message an admin reviews before launching.
 *
 * It is NOT on the preview response, and the shape I wrote by hand in #2938
 * said it was — carried over from an earlier proposal. The copy is org-wide
 * and per channel, from the request-copy preview, while this response is per
 * definition; one endpoint has no business restating the other's content.
 *
 * So it is passed in. The trimming is the part worth keeping: whitespace is
 * not a message, and an all-spaces body would otherwise satisfy the panel's
 * "there is something to review" check and unlock a bulk send behind a blank
 * preview — the exact outcome AC-11 exists to prevent, reached through a value
 * that merely looks non-empty.
 */
export const toMessagePreview = (
  copy: RenderedMessagePreview | undefined,
): RenderedMessagePreview | undefined => {
  const sms = copy?.smsBody?.trim() || undefined;
  const emailSubject = copy?.emailSubject?.trim() || undefined;
  const emailBody = copy?.emailBody?.trim() || undefined;

  if (!sms && !emailSubject && !emailBody) return undefined;

  return { smsBody: sms, emailSubject, emailBody };
};

/**
 * Preview DTO to the numbers the panel renders.
 *
 * `messagesToSend` is derived from the channel counts rather than read from a
 * field, because that is what it is: one patient reachable twice is two
 * messages. Deriving it keeps it from disagreeing with the breakdown beside it.
 */
export const toBulkLaunchPreviewData = (
  preview: BatchPreviewResponse,
  messagePreview?: RenderedMessagePreview,
): BulkLaunchPreviewData => ({
  eligiblePatients: preview.patients.eligible,
  alreadyCompletePatients: preview.patients.already_complete,
  excludedPatients: preview.patients.excluded,
  // Summed over whatever channels the server reports rather than naming sms
  // and email: a channel added later would otherwise be silently uncounted,
  // and this number is the one an admin reads as "how much is about to go out".
  messagesToSend: Object.values(preview.channels).reduce((sum, count) => sum + count, 0),

  /**
   * A truncated preview is a floor, so there is no audience to review and
   * nothing safe to launch. Withheld rather than disabled, like everything
   * else on this surface.
   */
  canLaunch: preview.truncated !== true,
  isTruncated: preview.truncated === true,

  exclusionBreakdown: exclusionBreakdownIsComplete(preview)
    ? Object.entries(preview.patients.excluded_by_reason ?? {})
        // Biggest first: the reason to fix is the one blocking the most people.
        .sort(([, a], [, b]) => b - a)
        .map(([reason, count]) => ({ reason, count }))
    : undefined,

  // The contract makes this page mandatory, so there is no absent-versus-empty
  // question left to get wrong here: an empty array means this page lists
  // nobody, which is what the panel will say.
  excludedPatientsPage: preview.excluded.map(item => ({
    patientId: item.patient_id,
    // Documented nullable. The panel falls back to the id rather than showing
    // a blank cell, which would read as a patient with no name.
    patientName: item.patient_display_name ?? undefined,
    // The primary reason, which is the one the counts are built from. The
    // full `reasons` list exists and is deliberately not shown: a patient
    // blocked on two counts still only needs one thing explained first.
    reason: item.primary_reason,
  })),

  messagePreview: toMessagePreview(messagePreview),
});

export interface BatchProgressData {
  batchId: string;
  state: string;
  counts: BatchMessageCounts;
  completedPatients?: number;
}

/**
 * Batch DTO to the progress panel's props.
 *
 * `completedPatients` stays undefined when the server does not report it. It is
 * NOT defaulted to zero: the panel renders an absent stat for undefined and a
 * real zero for zero, and those mean opposite things — "nobody has responded"
 * versus "we are not counting". A `?? 0` here would erase the distinction the
 * panel exists to preserve.
 */
export const toBatchProgressData = (batch: PostedBatch): BatchProgressData => ({
  batchId: batch.batch_id,
  state: batch.state,
  counts: {
    total: batch.counts.total_messages,
    queued: batch.counts.queued,
    sent: batch.counts.sent,
    delivered: batch.counts.delivered,
    failed: batch.counts.failed,
    cancelled: batch.counts.cancelled,
  },
  completedPatients: batch.completed_patient_count ?? undefined,
});
